import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ActiveCloudRun, CloudRunUnavailableReason } from "@mycompbox/shared";
import { SectionCard } from "@/components/new-look/blocks";
import { useActiveCloudRuns, useCloudRunStatus } from "@/hooks/queries/use-cloud-runs";
import { useAuthStore } from "@/stores/auth-store";
import { cn } from "@/lib/utils";

export type HoursState = "ok" | "warning" | "exhausted";

/** Where each fix lives: the AI keys and GitHub sections of Settings. */
export const AI_KEY_PATH = "/settings#ai";
export const GITHUB_PATH = "/settings#github";

export interface Fix {
  /** Plain-words instruction (a link label, or a note when `to` is null). */
  label: string;
  to: string | null;
}

export interface ReasonCopy {
  text: string;
  fix: Fix | null;
}

/** <80% ok, >=80% warning, >=100% exhausted. Zero or negative included hours never divide. */
export function hoursState(used: number, included: number): HoursState {
  if (included <= 0) return used > 0 ? "exhausted" : "ok";
  const ratio = used / included;
  if (ratio >= 1) return "exhausted";
  if (ratio >= 0.8) return "warning";
  return "ok";
}

/** Share of the hours used, 0-100, safe for zero included hours. */
export function hoursPercent(used: number, included: number): number {
  if (included <= 0) return used > 0 ? 100 : 0;
  return Math.max(0, Math.min(100, (used / included) * 100));
}

export function reasonCopy(reason: CloudRunUnavailableReason, isAdmin: boolean): ReasonCopy {
  switch (reason) {
    case "not_configured":
      return {
        text: "Cloud isn't set up for your company yet.",
        fix: isAdmin
          ? { label: "Open GitHub and cloud settings", to: GITHUB_PATH }
          : { label: "Ask an admin to set it up.", to: null },
      };
    case "no_key":
      return {
        text: "There's no AI key to run with.",
        fix: { label: "Add an AI key", to: AI_KEY_PATH },
      };
    case "key_provider_unsupported":
      return {
        text: "Your AI key can't be used in the cloud. Cloud runs need an Anthropic key.",
        fix: { label: "Change your AI key", to: AI_KEY_PATH },
      };
    case "repo_not_connected":
      return {
        text: "This project isn't connected to GitHub.",
        fix: isAdmin
          ? { label: "Connect GitHub", to: GITHUB_PATH }
          : { label: "Ask an admin to connect GitHub.", to: null },
      };
    case "hours_used_up":
      return {
        text: "Your company has used all its cloud hours this month.",
        fix: { label: "Hours reset on the 1st of the month.", to: null },
      };
    default: {
      const unreachable: never = reason;
      return unreachable;
    }
  }
}

/** Copy for projects without a GitHub connection (shown when the count is above zero). */
export function unconnectedCopy(count: number, isAdmin: boolean): ReasonCopy {
  return {
    text:
      count === 1
        ? "1 project isn't connected to GitHub yet."
        : `${String(count)} projects aren't connected to GitHub yet.`,
    fix: isAdmin
      ? { label: "Connect GitHub", to: GITHUB_PATH }
      : { label: "Ask an admin to connect GitHub.", to: null },
  };
}

function plural(n: number, word: string): string {
  return `${String(n)} ${word}${n === 1 ? "" : "s"}`;
}

/** 'Just started', '12 minutes', '2 hours 5 minutes'. */
export function elapsedLabel(startedAt: string, now: number): string {
  const start = Date.parse(startedAt);
  if (Number.isNaN(start)) return "";
  const minutes = Math.max(0, Math.floor((now - start) / 60_000));
  if (minutes < 1) return "Just started";
  if (minutes < 60) return plural(minutes, "minute");
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? plural(hours, "hour") : `${plural(hours, "hour")} ${plural(rest, "minute")}`;
}

function formatHours(n: number): string {
  return String(Math.round(n * 10) / 10);
}

const FIX_LINK = "text-sm font-medium text-primary hover:underline";

function FixView({ fix }: { fix: Fix }) {
  return fix.to ? (
    <Link to={fix.to} className={FIX_LINK}>
      {fix.label}
    </Link>
  ) : (
    <span className="text-sm text-muted-foreground">{fix.label}</span>
  );
}

const METER: Record<HoursState, { bar: string; note: string | null; noteClass: string }> = {
  ok: { bar: "bg-primary", note: null, noteClass: "" },
  warning: { bar: "bg-amber-500", note: "Almost used up", noteClass: "text-amber-700 dark:text-amber-400" },
  exhausted: { bar: "bg-red-500", note: "All included hours used", noteClass: "text-red-700 dark:text-red-400" },
};

function HoursMeter({ used, included }: { used: number; included: number }) {
  const state = hoursState(used, included);
  const m = METER[state];
  const label = `${formatHours(used)} of ${formatHours(included)} hours used this month`;
  return (
    <div className="mt-4 flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-foreground">{label}</span>
        {m.note ? <span className={cn("text-[13px] font-medium", m.noteClass)}>{m.note}</span> : null}
      </div>
      <div
        role="progressbar"
        aria-label="Cloud hours used this month"
        aria-valuemin={0}
        aria-valuemax={Math.max(included, 0)}
        aria-valuenow={Math.max(0, Math.min(used, Math.max(included, 0)))}
        aria-valuetext={label}
        className="h-2 w-full overflow-hidden rounded-full bg-muted"
      >
        <div className={cn("h-full rounded-full", m.bar)} style={{ width: `${String(hoursPercent(used, included))}%` }} />
      </div>
    </div>
  );
}

function runPath(run: ActiveCloudRun): string | null {
  if (run.epicId) return `/blueprints/${run.epicId}`;
  if (run.epicRequestId) return `/ideas/${run.epicRequestId}`;
  return null;
}

function isWaiting(run: ActiveCloudRun): boolean {
  return run.status === "waiting" || run.status === "parked";
}

function RunRow({ run, now }: { run: ActiveCloudRun; now: number }) {
  const to = runPath(run);
  const content = (
    <>
      <span
        aria-hidden="true"
        className={cn("h-2.5 w-2.5 shrink-0 rounded-full", isWaiting(run) ? "bg-amber-500" : "bg-emerald-500")}
      />
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{run.title}</span>
      <span className="shrink-0 text-[13px] text-muted-foreground">
        {isWaiting(run) ? "Waiting" : "Running"} · {elapsedLabel(run.startedAt, now)}
      </span>
    </>
  );
  return (
    <li>
      {to ? (
        <Link to={to} className="flex min-h-12 items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted">
          {content}
        </Link>
      ) : (
        <div className="flex min-h-12 items-center gap-3 px-2 py-2">{content}</div>
      )}
    </li>
  );
}

function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now());
    }, intervalMs);
    return () => {
      clearInterval(id);
    };
  }, [intervalMs]);
  return now;
}

export function YourCloud() {
  const status = useCloudRunStatus();
  const active = useActiveCloudRuns();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === "admin" || user?.isGlobalAdmin === true;
  const now = useNow();

  const s = status.data;
  const runs = active.data ?? [];

  let statusLine: ReadCopy | null = null;
  if (s) {
    if (s.available) statusLine = { text: "Ready to run in the cloud", fix: null, ready: true };
    else if (s.reason) statusLine = { ...reasonCopy(s.reason, isAdmin), ready: false };
    else statusLine = { text: "Cloud runs aren't available right now.", fix: null, ready: false };
  }
  const unconnected = s && s.unconnectedProjectCount > 0 ? unconnectedCopy(s.unconnectedProjectCount, isAdmin) : null;
  // Don't repeat the same advice when the reason already is about the GitHub connection.
  const showUnconnected = unconnected && s?.reason !== "repo_not_connected";

  return (
    <SectionCard title="Your Cloud">
      {status.isLoading ? (
        <div className="h-24 animate-pulse rounded-lg bg-muted" data-testid="your-cloud-loading" />
      ) : status.isError || !s || !statusLine ? (
        <p className="text-sm text-muted-foreground">Couldn't load your cloud status.</p>
      ) : (
        <>
          <div className="flex min-h-10 items-start gap-3 px-2">
            <span
              aria-hidden="true"
              className={cn("mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full", statusLine.ready ? "bg-emerald-500" : "bg-amber-500")}
            />
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-sm font-medium text-foreground">{statusLine.text}</span>
              {statusLine.fix ? <FixView fix={statusLine.fix} /> : null}
            </div>
          </div>
          {showUnconnected ? (
            <div className="mt-2 flex min-h-10 items-start gap-3 px-2">
              <span aria-hidden="true" className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-amber-500" />
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm text-foreground">{unconnected.text}</span>
                {unconnected.fix ? <FixView fix={unconnected.fix} /> : null}
              </div>
            </div>
          ) : null}
          <HoursMeter used={s.usedHours} included={s.includedHours} />
        </>
      )}

      <h3 className="mb-2 mt-5 text-sm font-semibold text-foreground">Building in the cloud</h3>
      {active.isLoading ? (
        <div className="h-12 animate-pulse rounded-lg bg-muted" />
      ) : active.isError ? (
        <p className="text-sm text-muted-foreground">Couldn't load what's building in the cloud.</p>
      ) : runs.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing is building in the cloud right now</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {runs.map((run) => (
            <RunRow key={run.id} run={run} now={now} />
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

interface ReadCopy extends ReasonCopy {
  ready: boolean;
}
